import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["attachTo", "children", "stepKey", "tourKey", "content", "childOnActive", "onClickCloseButton", "onOpenComplete", "onOpenStart", "open", "pulser", "scrollTo", "showCloseButton", "showSteps", "toggleOnTargetVisibilityChange", "beforeStart", "minimizable"];
let _ = t => t,
  _t;
import I18n from 'I18n';
import { createRef, Children, cloneElement, isValidElement, Component, Fragment } from 'react';
import { findDOMNode } from 'react-dom';
import PropTypes from 'prop-types';
import emptyFunction from 'react-utils/emptyFunction';
import { createGlobalStyle } from 'styled-components';
import UIPopover from 'UIComponents/tooltip/UIPopover';
import domElementPropType from 'UIComponents/utils/propTypes/domElement';
import UIFlex from 'UIComponents/layout/UIFlex';
import TourContext from '../contexts/TourContext';
import UITourPulser from '../pulser/UITourPulser';
import handleCallback from '../lib/handleCallback';
import { deactivateTour } from '../lib/deactivateTour';
import { getAttachedElement, isElementVisible } from '../utils/elementUtils';
import { waitForElementToAppear } from '../utils/observerUtils';
import { MinimizablePopover } from './components/MinimizablePopover';
import { MinimizedContent } from './components/MinimizedContent';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const UITourPulserGlobalStyle = createGlobalStyle(_t || (_t = _`
  html,
  body {
    overflow: hidden;
  }
`));
class UITourStep extends Component {
  constructor(props, context) {
    super(props);
    const {
      tour
    } = context || {};
    this.state = {};
    this._mounted = false;
    if (typeof tour !== 'undefined') {
      this.state = {
        currentTour: tour.getTour(),
        currentStep: tour.getStep(),
        readyToStart: false,
        minimized: false
      };
    }
    this.pulserRef = /*#__PURE__*/createRef();
    this.targetRef = /*#__PURE__*/createRef();
    this.onOpenComplete = this.onOpenComplete.bind(this);
    this.onOpenStart = this.onOpenStart.bind(this);
    this.scrollToTarget = this.scrollToTarget.bind(this);
    this.waitForAttachToElement = this.waitForAttachToElement.bind(this);
    this.unsubscribes = [];
  }
  componentDidMount() {
    const {
      beforeStart,
      tourKey,
      stepKey
    } = this.props;
    const {
      tour
    } = this.context;
    this._mounted = true;
    if (typeof tour !== 'undefined') {
      const unsubscribe = tour.subscribe((currentStep, currentTour) => {
        if (this._mounted) {
          this.setState({
            currentTour,
            currentStep
          });
        }
      });
      this.unsubscribes = this.unsubscribes.concat(unsubscribe);
      const currentTour = tour.getTour();
      const currentStep = tour.getStep();
      const isActive = this.isActive(tourKey, stepKey, currentTour, currentStep);

      // if the tour is active when we mount, we need to kick off the beforeStart.
      // This is because if this is already active on mount, there will be no state updates.
      // That means componentDidUpdate is never run.
      if (isActive) {
        handleCallback(beforeStart || this.waitForAttachToElement, () => {
          this.setState({
            readyToStart: true
          });
          this.childElement = this.getChildElement();
        }, {
          tourKey: currentTour,
          stepKey: currentStep
        });
      }
    }
    const {
      attachTo,
      toggleOnTargetVisibilityChange
    } = this.props;
    if (attachTo && toggleOnTargetVisibilityChange) {
      this.initTargetElementObserver();
    }
  }
  componentDidUpdate(prevProps, prevState) {
    const {
      beforeStart,
      tourKey,
      stepKey
    } = this.props;
    const {
      readyToStart,
      currentStep,
      currentTour
    } = this.state;
    const {
      readyToStart: wasReadyToStart
    } = prevState;
    const isActive = this.isActive(tourKey, stepKey, currentTour, currentStep);
    if (isActive && !wasReadyToStart && !readyToStart) {
      handleCallback(beforeStart || this.waitForAttachToElement, () => {
        this.setState({
          readyToStart: true
        });
      }, {
        tourKey: currentTour,
        stepKey: currentStep
      });
    }
    if (!isActive && wasReadyToStart && readyToStart) {
      this.setState({
        readyToStart: false
      });
    }
    if (isActive) {
      this.childElement = this.getChildElement();
    }
    const {
      toggleOnTargetVisibilityChange,
      minimizable
    } = this.props;
    if (toggleOnTargetVisibilityChange && readyToStart !== wasReadyToStart) {
      if (readyToStart) {
        this.beginTargetElementObserver();
      } else {
        this.endTargetElementObserver();
      }
    }
    if (readyToStart && minimizable) {
      this.minimizeTimer = setTimeout(() => {
        this.setState({
          minimized: true
        });
      }, 8000);
    }
    if (minimizable === false && this.minimizeTimer) {
      clearTimeout(this.minimizeTimer);
    }
  }
  componentWillUnmount() {
    const {
      minimizable
    } = this.props;
    const {
      tour
    } = this.context;
    this._mounted = false;
    if (typeof tour !== 'undefined') {
      this.unsubscribes.forEach(unsubscribe => unsubscribe());
    }
    this.endTargetElementObserver();
    if (minimizable) {
      clearTimeout(this.minimizeTimer);
    }
  }
  getChildElement() {
    const {
      pulser,
      children
    } = this.props;
    return pulser && children ? findDOMNode(this) : undefined;
  }

  /**
   * Setup MutationObserver for hide/resume tooltip when target element is removed/re-mounted
   */
  initTargetElementObserver() {
    const {
      attachTo,
      tourKey,
      stepKey
    } = this.props;
    this.targetObserver = new MutationObserver(() => {
      const {
        readyToStart
      } = this.state;
      if (!readyToStart || !this.isActive(tourKey, stepKey)) {
        return;
      }
      const target = getAttachedElement(attachTo);
      const isTargetVisible = isElementVisible(target);
      // If target is removed/resumed or visibility has changed, re-render tour step to hide/resume tour step
      if (this.wasTargetVisible !== isTargetVisible) {
        // Re-render tour step
        this.setState({});
        this.wasTargetVisible = isTargetVisible;
      }
    });
  }
  beginTargetElementObserver() {
    if (!this.targetObserver) {
      return;
    }
    this.targetObserver.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true
    });
  }
  endTargetElementObserver() {
    if (!this.targetObserver) {
      return;
    }
    this.targetObserver.disconnect();
    this.wasTargetVisible = undefined;
  }
  isActive(tourKey, stepKey, currentTourKey, currentStepKey) {
    const {
      currentTour,
      currentStep
    } = this.state;
    currentTourKey = currentTourKey || currentTour;
    currentStepKey = currentStepKey || currentStep;
    const isOnTour = tourKey ? tourKey === currentTourKey : !!currentTourKey;
    const isOnStep = stepKey === currentStepKey;
    return isOnTour && isOnStep;
  }
  getStepText({
    steps,
    stepKey
  }) {
    const stepIndex = steps.indexOf(stepKey);
    const totalSteps = steps.length;
    return I18n.text('shepherd-react.stepProgress', {
      currentStep: stepIndex + 1,
      totalSteps
    });
  }
  onOpenComplete() {
    const {
      onOpenComplete
    } = this.props;
    if (onOpenComplete) {
      handleCallback(onOpenComplete, this.scrollToTarget, this.context);
    }
  }
  onOpenStart() {
    const {
      onOpenStart,
      stepKey
    } = this.props;
    if (onOpenStart) {
      onOpenStart();
    }
    if (this.pulserRef.current && this.pulserRef.current.showPulser) {
      this.pulserRef.current.showPulser();
    }
    const popover = document.getElementById(`${stepKey}-popover`);
    if (popover) {
      popover.focus({
        preventScroll: true
      });
    }
  }
  scrollToTarget() {
    const {
      attachTo,
      scrollTo
    } = this.props;
    if (!scrollTo || this._scrolled) {
      return;
    }
    const target = getAttachedElement(attachTo) || this.targetRef.current;
    if (target && target.scrollIntoView) {
      target.scrollIntoView({
        behavior: 'smooth',
        block: scrollTo
      });
      this._scrolled = true;
    }
  }
  waitForAttachToElement() {
    const {
      attachTo
    } = this.props;
    if (!attachTo || getAttachedElement(attachTo)) {
      return undefined;
    }
    const isElementGetter = typeof attachTo === 'function';
    const elementGetter = isElementGetter ? attachTo : undefined;
    const elementQuery = !isElementGetter ? attachTo : undefined;
    return waitForElementToAppear({
      elementGetter,
      elementQuery
    });
  }
  renderChildren(target) {
    const {
      childOnActive,
      pulser,
      scrollTo,
      stepKey,
      children,
      tourKey
    } = this.props;
    const showScrollToTarget = scrollTo && !target;
    const showChildOnActive = this.state.readyToStart && childOnActive;
    const showPulser = this.state.readyToStart && pulser;
    const isActive = this.isActive(tourKey, stepKey);
    // Using isActive instead of readyToStart here because global style needs to be appended before `beforeStart` hook and `scrollToTarget`
    const shouldPreventOverflow = isActive && typeof pulser === 'object' && pulser.preventOverflow === true;
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [children, showScrollToTarget && /*#__PURE__*/_jsx("span", {
        ref: this.targetRef
      }), showChildOnActive && childOnActive, showPulser && /*#__PURE__*/_jsx(UITourPulser, Object.assign({
        ref: this.pulserRef,
        target: target || this.childElement
      }, typeof pulser === 'object' && pulser)), shouldPreventOverflow && /*#__PURE__*/_jsx(UITourPulserGlobalStyle, {})]
    }, stepKey);
  }
  render() {
    const _this$props = this.props,
      {
        attachTo,
        children,
        stepKey,
        tourKey,
        content,
        onClickCloseButton,
        showCloseButton,
        showSteps

        // beforeStart shouldn't be passed to UIPopover
        ,

        minimizable
      } = _this$props,
      extraProps = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      readyToStart
    } = this.state;
    const {
      tour,
      rootNode
    } = this.context;
    const target = getAttachedElement(attachTo) || null;
    const hasTarget = attachTo ? !!target && isElementVisible(target) : true;
    const isActive = this.isActive(tourKey, stepKey) && hasTarget && readyToStart;
    if (typeof content === 'undefined' && children) {
      const child = Children.only(children);
      return /*#__PURE__*/cloneElement(child, {
        isStepActive: isActive
      });
    }
    if ( /*#__PURE__*/isValidElement(content) && isActive) {
      return content;
    }
    const config = tour.getConfig();
    const stepText = showSteps && this.getStepText({
      steps: config.steps,
      stepKey
    });
    const handleDeactivation = () => {
      if (typeof onClickCloseButton === 'function' && onClickCloseButton(stepKey, tourKey) === false) {
        return null;
      }
      return deactivateTour(tour, tourKey);
    };
    const {
      open = isActive
    } = this.props;

    // For some reason UIDemoTourStep fails when MinimizablePopover is the default element
    // so we conditionally render different UIPopOver elements based on minimizable
    // Additionally this will make sure that we reduce possible regressions
    const PopoverElement = minimizable ? MinimizablePopover : UIPopover;
    return /*#__PURE__*/_jsx(PopoverElement
    // @ts-expect-error _bodyElement is not typed
    , Object.assign({
      _bodyElement: rootNode,
      "aria-labelledby": `${stepKey}-popover-title`,
      "aria-live": "assertive",
      id: `${stepKey}-popover`,
      role: "alertdialog",
      tabIndex: 0,
      use: "shepherd",
      open: open,
      onMouseEnter: () => {
        if (minimizable) {
          clearTimeout(this.minimizeTimer);
        }
      },
      width: 350,
      target: target
      // Unfortunately transient props don't work with UIPopover which is why
      // minimize is passed as an attribute accepting a number or undefined
      ,
      minimize: this.state.minimized ? +this.state.minimized : undefined,
      content: Object.assign({
        step: stepText
      }, typeof content === 'object' && !this.state.minimized ? content : undefined, {
        header: content && typeof content === 'object' && 'header' in content ? /*#__PURE__*/_jsx(UIFlex, {
          id: `${stepKey}-popover-title`,
          align: "start",
          children: minimizable ? /*#__PURE__*/_jsx(MinimizedContent, {
            tourKey: this.state.currentTour || '',
            stepKey: this.state.currentStep || '',
            minimized: this.state.minimized,
            onClickEnlarge: () => this.setState({
              minimized: false
            }),
            onClickMinimize: () => this.setState({
              minimized: true
            }),
            children: content.header
          }) : content.header
        }) : undefined
      }),
      onOpenComplete: this.onOpenComplete,
      onOpenStart: this.onOpenStart,
      closeOnOutsideClick: false,
      showCloseButton: showCloseButton
      // showCloseButton in UIPopover triggers onOpenChange when clicked. Set onOpenChange to
      // end tour if showCloseButton is provided, otherwise leave unset since used otherwise.
      ,
      onOpenChange: showCloseButton ? handleDeactivation : undefined
    }, extraProps, {
      children: this.renderChildren(target)
    }));
  }
}
UITourStep.contextType = TourContext;
UITourStep.defaultProps = {
  onOpenComplete: emptyFunction
};
UITourStep.propTypes = {
  tourKey: PropTypes.string,
  stepKey: PropTypes.string.isRequired,
  beforeStart: PropTypes.func,
  childOnActive: PropTypes.node,
  children: PropTypes.node,
  onClickCloseButton: PropTypes.func,
  onOpenComplete: PropTypes.func,
  onOpenStart: PropTypes.func,
  open: PropTypes.bool,
  pulser: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape({
    attachTo: PropTypes.oneOfType([PropTypes.func, PropTypes.string, PropTypes.arrayOf(PropTypes.func), PropTypes.arrayOf(PropTypes.string)]),
    borderColor: PropTypes.string,
    borderRadius: PropTypes.number,
    borderSize: PropTypes.number,
    enableBorder: PropTypes.bool,
    enableOverlay: PropTypes.bool,
    enablePulse: PropTypes.bool,
    pulserDistance: PropTypes.number,
    pulserDuration: PropTypes.string,
    pulserPadding: PropTypes.number,
    pulserZIndex: PropTypes.number
  })]),
  attachTo: PropTypes.oneOfType([PropTypes.string, PropTypes.func, domElementPropType]),
  content: PropTypes.oneOfType([PropTypes.node, PropTypes.shape({
    header: PropTypes.node,
    body: PropTypes.node,
    footer: PropTypes.node,
    step: PropTypes.node
  })]),
  scrollTo: PropTypes.oneOf(['start', 'center', 'end', 'nearest']),
  showCloseButton: PropTypes.bool,
  showSteps: PropTypes.bool,
  toggleOnTargetVisibilityChange: PropTypes.bool,
  minimizable: PropTypes.bool
};
export default UITourStep;